import { Section, Sub, Card, Note, Fields, Shot, New } from '../../../components/docs/Blocks';

export default function Settings() {
  return (
    <Section
      id="settings"
      title="Settings"
      lead="Changes apply immediately and are saved automatically. There is no OK button and nothing to restart."
    >
      <Sub id="settings-language" title="Language">
        <Shot name="settings" alt="The Settings screen showing language, backup and network options" />
        <Card>
          <p>
            Toolchest ships in 21 languages, including right-to-left ones. The default
            follows your operating system where a matching translation exists, and
            switching takes effect straight away without a restart.
          </p>
          <p>
            A language that is only partly translated says so and how many strings are
            still missing; those appear in English rather than as blanks.
          </p>
          <p>
            <strong>Open Languages Folder</strong> <New>New in 3.0.4</New> shows the
            translation files themselves, one JSON file per language. Edit one and
            restart Toolchest to see your changes. A file you have changed is yours from
            then on: updates leave it alone, while the files you have not touched are
            refreshed with newer translations automatically.
          </p>
          <Note>
            Translations are always welcome. If you have improved a language, or added
            one, send the file over on Discord.
          </Note>
        </Card>
      </Sub>

      <Sub id="settings-backups" title="Backups">
        <Card>
          <Fields
            items={[
              ['Back up before saving', 'Always, Ask each session, or Never. Always is the default.'],
              ['Backups to keep per world', 'Five by default. Locked and manual backups do not count, and the newest is always kept.'],
              ['Disk space per world', 'Ten gigabytes by default. Old backups are removed only once both this and the count are exceeded.'],
              ['Backup location', 'Where new backups are written. The default is a folder in your app data, outside every world. Choose your own — an external drive, say — or reset to the default at any time.'],
            ]}
          />
          <Note>
            Changing the location only affects backups taken from then on: the ones
            already written stay where they are, and are neither moved nor deleted.
            Toolchest refuses a folder inside a Minecraft world, because backups kept
            in there go when the world does — which is the whole problem version 2 had.
          </Note>
        </Card>
      </Sub>

      <Sub id="settings-updates" title="Updates">
        <Card>
          <p>
            The current version, and a button to check for a newer one. Toolchest also
            checks quietly a few seconds after launch and shows a dismissable notice
            only when there is something newer — a check that fails, or finds nothing,
            says nothing.
          </p>
          <p>
            The check sends a version string and nothing else. No machine identifier,
            no world data, no counts.
          </p>
        </Card>
      </Sub>

      <Sub id="settings-network" title="Network">
        <Card>
          <Fields
            items={[
              ['Player appearance previews', 'Fetches player heads and 3D models from minecraftrender.com so the Player Editor can show who it is editing. Only the resolved or manually entered player name is sent, and head images are cached on disk. Turn it off and Toolchest makes no requests other than the update check, unless you use Realms & Servers.'],
              ['Realms & Servers', 'New in 3.1. Signing in for Realms talks to Microsoft, Xbox Live and the Realms service; a server you add is reached over SFTP at the address you give, and its hosting panel if you name one. None of it happens until you use that section.'],
              ['Keep Realm copies after uploading', 'New in 3.1. Off by default, so a Realm’s downloaded copy is deleted once the Realm has it. A failed upload never deletes anything.'],
              ['Cached player heads', 'Head images stay on disk until cleared, so a player who changes their skin keeps their old head here. Clear cache removes them all and they are fetched fresh the next time each one is shown. To refresh one player rather than all of them, use the button on their Identity card in the Player Editor.'],
            ]}
          />
        </Card>
      </Sub>

      <Sub id="settings-storage" title="Where Toolchest keeps things">
        <Card>
          <p>
            The last panel lists the folders the program uses, each with a path you can
            open: the settings file, the backups folder, the restore points and the
            logs. Nothing is hidden, and nothing is written inside your world folders.
          </p>
          <p>
            Since 3.0.4 Toolchest also tidies up after itself when it starts: restore
            points a crash left behind go after a week, add-ons you removed after a
            day, and empty backup folders straight away. Backups themselves are never
            touched — those are yours to delete.
          </p>
        </Card>
      </Sub>

      <Sub id="settings-theme" title="Light and dark">
        <Card>
          <p>
            The theme toggle is the sun and moon button in the title bar rather than a
            setting, because it is something people flick rather than configure. Your
            choice is remembered.
          </p>
        </Card>
      </Sub>
    </Section>
  );
}
